import { publish } from '../../EventsBus';
import { MediaDomainEvents } from '../domain';

export const requestMediaPermissions = async (): Promise<void> => {
	try {
		const { getUserMedia } = await import('@opentok/client');
		getUserMedia()
			.then(
				(stream) => {
					stream.getTracks().forEach((track) => track.stop());
					publish(MediaDomainEvents.acceptedMediaPermissions);
				},
				() => {
					publish(MediaDomainEvents.notAcceptedMediaPermissions);
				}
			)
			.catch(() => {
				publish(MediaDomainEvents.deniedMediaPermissions);
			});
	} catch (e) {
		publish(MediaDomainEvents.blockedMediaPermissions);
	}
};

export const stopTracks = async () => {
	const { getUserMedia } = await import('@opentok/client');
	getUserMedia().then((stream) => {
		stream.getTracks().forEach(function (track) {
			track.stop();
		});
	});
};

export const getFirstCameraDevice = async (): Promise<MediaDeviceInfo | null> => {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return devices.find((device) => device.kind === 'videoinput' && device.label !== '') || null;
	} catch (error) {
		console.error('Error getting camera device:', error);
		throw error;
	}
};

export const getFirstMicDevice = async (): Promise<MediaDeviceInfo | null> => {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return devices.find((device) => device.kind === 'audioinput' && device.label !== '') || null;
	} catch (error) {
		console.error('Error getting microphone device:', error);
		throw error;
	}
};

export const getFirstAudioDevice = async (): Promise<MediaDeviceInfo | null> => {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return devices.find((device) => device.kind === 'audiooutput' && device.label !== '') || null;
	} catch (error) {
		console.error('Error getting audio device:', error);
		throw error;
	}
};

export const hasCameraPermissionEnabled = async (): Promise<boolean> => {
	return !!(await getFirstCameraDevice());
};

export const hasMicrophonePermissionEnabled = async (): Promise<boolean> => {
	return !!(await getFirstMicDevice());
};

export const checkDeviceAvailability = async (): Promise<{ camera: boolean; microphone: boolean; error?: string }> => {
	try {
		const stream = await navigator.mediaDevices.getUserMedia({
			video: true,
			audio: true
		});

		stream.getTracks().forEach(track => track.stop());

		return { camera: true, microphone: true };
	} catch (error: any) {
		console.error('Device availability check failed:', error);

		if (error.name === 'AbortError' || error.name === 'NotReadableError') {
			return {
				camera: false,
				microphone: false,
				error: 'Devices already in use'
			};
		}

		if (error.name === 'NotAllowedError') {
			return {
				camera: false,
				microphone: false,
				error: 'Permissions not allowed'
			};
		}

		return {
			camera: false,
			microphone: false,
			error: 'Unknown error'
		};
	}
};

export const getDevices = async () => {
	if (!navigator.mediaDevices?.enumerateDevices) {
		publish(MediaDomainEvents.enumerateDevicesNotSupported);
		return;
	}

	const devices = {
		audioinput: <MediaDeviceInfo[]>[],
		audiooutput: <MediaDeviceInfo[]>[],
		videoinput: <MediaDeviceInfo[]>[],
	};

	try {
		const devicesInfo = await navigator.mediaDevices.enumerateDevices();

		for (const device of devicesInfo) {
			devices[`${device.kind}`].push(device);
		}

		if (devicesInfo.length === 0) {
			publish(MediaDomainEvents.emptyListMediaDevices);
		}
	} catch (e) {
		publish(MediaDomainEvents.cannotListMediaDevices);
		return null;
	}

	return devices;
};

export function selectAudioInputDevice(deviceId: string) {
	if (window.stream) {
		window.stream.getTracks().forEach((track) => {
			track.stop();
		});
	}

	const constraints: MediaStreamConstraints = {
		audio: deviceId ? { deviceId: { exact: deviceId } } : undefined,
	};

	navigator.mediaDevices
		.getUserMedia(constraints)
		.then(() => {
			publish(MediaDomainEvents.changedMicDevice);
		})
		.catch(() => {
			publish(MediaDomainEvents.cannotChangeMicDevice);
		});
}

export function selectVideoInputDevice(deviceId: string) {
	if (window.stream) {
		window.stream.getTracks().forEach((track) => {
			track.stop();
		});
	}
	const constraints: MediaStreamConstraints = {
		video: deviceId ? { deviceId: { exact: deviceId } } : undefined,
	};
	navigator.mediaDevices
		.getUserMedia(constraints)
		.then(() => {
			publish(MediaDomainEvents.changedCamDevice);
		})
		.catch(() => {
			publish(MediaDomainEvents.cannotChangeCamDevice);
		});
}

